<script>
// utils and composables
import { login } from '../../api/auth';
import { mapGetters } from 'vuex';
import { parseBoolean } from 'shared/utils/index.js';
import { useAlert } from 'dashboard/composables';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';

// mixins
import globalConfigMixin from 'shared/mixins/globalConfigMixin';

// components
import FormInput from '../../components/Form/Input.vue';
import PasswordInput from '../../components/Form/PasswordInput.vue';
import CheckBox from '../../components/Form/CheckBox.vue';
import GoogleOAuthButton from '../../components/GoogleOauth/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import SubmitButton from '../../components/Button/SubmitButton.vue';

const ERROR_MESSAGES = {
  'no-account-found': 'LOGIN.OAUTH.NO_ACCOUNT_FOUND',
  'business-account-only': 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY',
};

export default {
  components: {
    FormInput,
    PasswordInput,
    GoogleOAuthButton,
    Spinner,
    SubmitButton,
    CheckBox,
  },
  mixins: [globalConfigMixin],
  props: {
    ssoAuthToken: { type: String, default: '' },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      // We need to initialize the component with any
      // properties that will be used in it
      credentials: {
        email: '',
        password: '',
      },
      loginApi: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      error: '',
      rememberMe: false,
    };
  },
  validations() {
    return {
      credentials: {
        password: {
          required,
        },
        email: {
          required,
          email,
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    logoSrc() {
      return this.globalConfig.logo;
    },
    showGoogleOAuth() {
      return Boolean(window.hichatConfig.googleOAuthClientId);
    },
    showSignupLink() {
      return parseBoolean(window.hichatConfig.signupEnabled);
    },
  },
  created() {
    if (this.ssoAuthToken) {
      this.submitLogin();
    }
    if (this.authError) {
      const message = ERROR_MESSAGES[this.authError] ?? 'LOGIN.API.UNAUTH';
      useAlert(this.$t(message), {type: 'error'});
      // wait for idle state
      this.requestIdleCallbackPolyfill(() => {
        // Remove the error query param from the url
        const { query } = this.$route;
        this.$router.replace({ query: { ...query, error: undefined } });
      });
    }
  },
  methods: {
    // TODO: Remove this when Safari gets wider support
    // Ref: https://caniuse.com/requestidlecallback
    //
    requestIdleCallbackPolyfill(callback) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
      } else {
        // Fallback for safari
        // Using a delay of 0 allows the callback to be executed asynchronously
        // in the next available event loop iteration, similar to requestIdleCallback
        setTimeout(callback, 0);
      }
    },
    showAlertMessage(message) {
      // Reset loading, current selected agent
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      useAlert(this.loginApi.message, {type: 'success'});
    },
    submitLogin() {
      this.loginApi.hasErrored = false;
      this.loginApi.showLoading = true;

      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
      };

      login(credentials)
        .then(() => {
          this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        })
        .catch(response => {
          // Reset URL Params if the authentication is invalid
          if (this.email) {
            window.location = '/app/login';
          }
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },
    submitFormLogin() { 
      this.loginApi.hasErrored = false;
      const emailValue = this.email ? decodeURIComponent(this.email) : this.credentials.email;
      if (!emailValue || emailValue.trim() === '') {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.REQUIRED'));
        return;
      }
      if (this.v$.credentials.email.$invalid && !this.email) {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.ERROR'));
        return;
      }
      if (this.v$.credentials.password.$invalid) {
        this.showAlertMessage(this.$t('LOGIN.PASSWORD.REQUIRED'));
        return;
      }
      this.submitLogin();
    },
    onInput(field) {
    if (this.credentials[field]) {
      this.credentials[field] = this.credentials[field].replace(/\s/g, '');
    }
    if (this.v$ && this.v$.credentials && this.v$.credentials[field]) {
      this.v$.credentials[field].$touch();
    }
  },
  },
};
</script>

<template>
  <main
    class="flex flex-col w-full min-h-screen py-20 bg-hichat-25 sm:px-6 lg:px-8 dark:bg-[#181D27]"
  >
    <section
      class="bg-white text shadow-lg rounded-xl w-[360px] h-[445px] mx-auto mt-8 px-6 py-6 sm:shadow-lg"
      :class="{
        'mb-8 mt-15': !showGoogleOAuth,
        'animate-wiggle': loginApi.hasErrored,
      }"
    >
      <div v-if="!email">
        <div class="flex items-center justify-center mb-8">
          <img
            :src="logoSrc"
            :alt="globalConfig.installationName"
            class="h-10 w-auto object-contain"
          />
        </div>
        
        <GoogleOAuthButton v-if="showGoogleOAuth" />
        <form class="space-y-6" novalidate @submit.prevent="submitFormLogin">
          <div class="space-y-2">
            <label class="block text-sm text-gray-600 font-semibold">
              {{ $t('LOGIN.EMAIL.LABEL') }}
            </label>
          <FormInput 
            autocomplete="email"
            class="h-[40px] py-2 px-[14px] !text-n-gray-7 tracking-wide !bg-white"        
            v-model="credentials.email"
            name="email_address"  
            type="text"
            data-testid="email_input"
            :tabindex="1"
            required
            :placeholder="$t('LOGIN.EMAIL.PLACEHOLDER')"
            :has-error="v$.credentials.email.$error"
            @input="onInput('email')"
          />
          </div>
          <PasswordInput
            v-model="credentials.password"
            :label="$t('LOGIN.PASSWORD.LABEL')"
            name="password"
            data-testid="password_input"
            :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
            :has-error="v$.credentials.password.$error"
            :tabindex="2"
            autocomplete="current-password"
            class="h-[40px] py-2 px-[14px] !text-n-gray-7 tracking-wide !bg-white"
            @input="onInput('password')"
          />
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <input
                id="remember-me"
                v-model="rememberMe"
                name="remember-me"
                type="checkbox"
                class="custom-checkbox h-4 w-4"
              />
              <label
                for="remember-me"
                class="ml-2 block text-sm text-[#344054] font-medium cursor-pointer select-none"
              >
                {{ $t('LOGIN.REMEMBER_ME') }}
              </label>
            </div>
            <router-link
              v-if="!globalConfig.disableUserProfileUpdate"
              to="auth/reset/password"
              class="text-n-gray-5 font-medium text-sm"
              tabindex="4"
            >
              {{ $t('LOGIN.FORGOT_PASSWORD') }}
            </router-link>
          </div>
          <SubmitButton
            :disabled="loginApi.showLoading"
            :tabindex="3"
            :button-text="$t('LOGIN.SUBMIT')"
            :loading="loginApi.showLoading"
            button-class="!bg-hilab hover:opacity-90 h-[40px]"
          />
        </form>        
        <div class="flex justify-between mt-8">
          <span class="text-n-gray-800 text-sm ">
            {{ $t('LOGIN.NO_ACCOUNT_YET') }}
          </span>
          <router-link 
            to="auth/signup" 
            class="text-n-gray-5 text-sm font-medium"
          >
            {{ $t('LOGIN.REGISTER_BUSINESS') }}
          </router-link>
        </div>
      </div>
      <div v-else class="flex items-center justify-center">
        <Spinner color-scheme="primary" size="" />
      </div>
    </section>
  </main>
</template>

<style scoped>
.custom-checkbox {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-color: #F9F5FF;
  border: 1px solid #D5D7DA; /* gray-300 */
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease-in-out;
}



.custom-checkbox:checked::after {
  content: '';
  position: absolute;
  left: 5.5px;
  top: 3px;
  width: 4px;
  height: 8px;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}



:deep(input[data-testid="email_input"]),
:deep(input[data-testid="password_input"]) {
  border-color: #F5F5F5 !important;
  transition: border-color 180ms ease-in-out;
}

:deep(input[data-testid="email_input"]:focus),
:deep(input[data-testid="password_input"]:focus) {
  border-color: #12B76A !important;
}


</style>
